import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { supabase } from "../../../integrations/supabase/client";
import { Button } from "../../../components/ui/button";

export const Route = createFileRoute("/auth/callback/")({
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("Processing authentication...");

  useEffect(() => {
    if (typeof window === "undefined" || !supabase) return;
    const sb = supabase;

    const handleCallback = async () => {
      try {
        const { data, error } = await sb.auth.getSession();

        if (error) {
          setStatus("error");
          setMessage(error.message);
          return;
        }

        if (data.session) {
          setStatus("success");
          setMessage("Authentication successful! Redirecting...");
          setTimeout(() => navigate({ to: "/dashboard" }), 1500);
        } else {
          const url = new URL(window.location.href);
          const code = url.searchParams.get("code");

          if (code) {
            const { error: exchangeError } = await sb.auth.exchangeCodeForSession(code);
            if (exchangeError) {
              setStatus("error");
              setMessage(exchangeError.message);
            } else {
              setStatus("success");
              setMessage("Authentication successful! Redirecting...");
              setTimeout(() => navigate({ to: "/dashboard" }), 1500);
            }
          } else {
            setStatus("error");
            setMessage("No authentication data found. Please try signing in again.");
          }
        }
      } catch (_err) {
        setStatus("error");
        setMessage("An unexpected error occurred during authentication.");
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6 text-center">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Authentication
        </h1>

        {status === "loading" && (
          <div className="space-y-4">
            <div className="text-4xl animate-spin">⏳</div>
            <p className="text-muted-foreground">{message}</p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-4">
            <div className="text-4xl">✅</div>
            <p className="text-green-600 dark:text-green-400">{message}</p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4">
            <div className="text-4xl">❌</div>
            <p className="text-destructive">{message}</p>
            <Button variant="outline" asChild>
              <Link to="/auth">Back to Sign In</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
